<script setup lang="ts">
import { computed, ref } from "vue";

import {
  DownloadOutlined as GetAppIcon,
  PauseFilled as PauseIcon,
  PlayArrowFilled as PlayArrowIcon,
} from "@vicons/material";
import {
  NButton,
  NCard,
  NConfigProvider,
  NIcon,
  NSelect,
  NSlider,
  NSpin,
  NTooltip,
} from "naive-ui";

import { useTheme } from "@/utils/theme";

import Volume from "./Volume.vue";
import { useAudio } from "./hooks/useAudio";
import { sec2str } from "./utils/time";

type Quality = "wav" | "mp3";

const props = defineProps({
  name: String,
  title: String,
  lowSource: String,
  highSource: String,
  p: Number,
});

const quality = ref<Quality>("wav");
const { theme, themeOverrides, isDark } = useTheme();

const low = useAudio(props.lowSource || "", props.p || 0);
const high = useAudio(props.highSource || "", props.p || 0);

const qualityOptions: { label: Quality; value: Quality }[] = [
  { label: "wav", value: "wav" },
  { label: "mp3", value: "mp3" },
];

const currentAudio = computed(() => {
  return quality.value === "mp3" ? low : high;
});

const playAble = computed(() => {
  return (
    currentAudio.value.state.value !== "idle" &&
    currentAudio.value.state.value !== "loading"
  );
});

const playIconToolTip = computed(() => {
  switch (currentAudio.value.state.value) {
    case "idle": {
      return "开始加载";
    }
    case "loading": {
      return "加载中...";
    }
    case "loaded": {
      return "播放";
    }
    case "playing": {
      return "暂停";
    }
    case "paused": {
      return "继续播放";
    }
    default: {
      return "";
    }
  }
});

function playIconHandler() {
  console.log("playIconHandler", currentAudio.value.state);
  switch (currentAudio.value.state.value) {
    case "idle": {
      currentAudio.value.load();
      break;
    }

    case "loaded": {
      currentAudio.value.play();
      break;
    }

    case "playing": {
      currentAudio.value.pause();
      break;
    }

    case "paused": {
      currentAudio.value.play();
      break;
    }
  }
}

function handleSeek(value: number) {
  currentAudio.value.seek(value);
}

function handleQualityChange(value: Quality) {
  low.stopped.value = value === "wav";
  high.stopped.value = value === "mp3";

  quality.value = value;
}
</script>

<template>
  <NConfigProvider
    preflight-style-disabled
    :theme="theme"
    :theme-overrides="themeOverrides"
  >
    <NCard
      :bordered="false"
      :class="['audio-player', isDark && 'prts-widget-dark']"
    >
      <div v-if="name || title" class="header">
        <div v-if="title" class="title" v-html="title"></div>
        <div v-else class="title">{{ name }}</div>
      </div>

      <div class="slider-group">
        <div class="time-display">
          {{ sec2str(currentAudio.process.value) }}/{{
            sec2str(currentAudio.len.value)
          }}
        </div>
        <NSlider
          class="progress-slider"
          :disabled="!playAble"
          :value="currentAudio.process.value"
          :min="0"
          :format-tooltip="sec2str"
          :max="currentAudio.len.value"
          @update:value="handleSeek"
        />
      </div>

      <div class="control-bar">
        <NTooltip>
          <template #trigger>
            <NSpin :show="currentAudio.state.value === 'loading'" size="small">
              <NButton
                quaternary
                circle
                :disabled="currentAudio.state.value === 'loading'"
                @click="playIconHandler"
              >
                <template #icon>
                  <NIcon v-if="currentAudio.state.value === 'playing'">
                    <PauseIcon />
                  </NIcon>
                  <NIcon v-else>
                    <PlayArrowIcon />
                  </NIcon>
                </template>
              </NButton>
            </NSpin>
          </template>
          {{ playIconToolTip }}
        </NTooltip>

        <NTooltip :disabled="!playAble">
          <template #trigger>
            <NButton
              quaternary
              circle
              :disabled="!playAble"
              @click="currentAudio.changeLoop()"
            >
              <template #icon>
                <span
                  v-if="currentAudio.loop.value"
                  class="mdi mdi-autorenew"
                />
                <span v-else class="mdi mdi-autorenew-off" />
              </template>
            </NButton>
          </template>
          {{ currentAudio.loop.value ? "循环播放" : "顺序播放" }}
        </NTooltip>

        <Volume
          v-model:vol="currentAudio.vol.value"
          v-model:mute="currentAudio.mute.value"
        />

        <NButton quaternary circle @click="currentAudio.download()">
          <template #icon>
            <NIcon>
              <GetAppIcon />
            </NIcon>
          </template>
        </NButton>

        <NSelect
          class="quality-select"
          :value="quality"
          :options="qualityOptions"
          @update:value="handleQualityChange"
        />
      </div>
    </NCard>
  </NConfigProvider>
</template>

<style scoped>
@import "@/styles/dark-mode.scss";
.audio-player {
  max-width: 800px;
  padding: 16px;
}

.header {
  padding: 0;
  margin-bottom: 12px;
}

.title {
  font-size: 1.25rem;
  font-weight: 500;
}

.slider-group {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.time-display {
  min-width: 80px;
  margin-right: 12px;
}

.progress-slider {
  flex: 1;
}

.control-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quality-select {
  width: 80px;
  margin-left: 8px;
}
</style>
